import { addGameObject } from './entity';
import { COLORS, LINES, SHAPE_STYLES } from './constants';
import {
  type ServerEvent_Shape,
  ObjectType,
  ShapeType,
  ShapePresetStyle,
  LineEndpointType,
} from './shared/api_shared_types';
import { setParentByEntityProps, setVisibilityState } from './simulate';
import {
  VisibilityState,
  type Color,
  type GameManager,
  type GameObjectController,
  type ShapeObject,
  type Entity,
  type RenderShapeStyle,
  RenderItemType,
  type V2,
} from './types';
import { hexToColor, v2 } from './helpers';
import {
  buildClosedArrow,
  buildLineDot,
  buildOpenArrow,
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageCircle,
  type RenderPackageItem,
  type RenderPackageLine,
  type RenderPackageRect,
} from './render_package';
import logger from './controllers/logger';

const PRESET_STYLES: Record<ShapePresetStyle, RenderShapeStyle> = {
  [ShapePresetStyle.WHITE_FILLED]: {
    ...SHAPE_STYLES.NONE,
    fillColor: COLORS.WHITE_100,
  },
  [ShapePresetStyle.WHITE_BORDER]: {
    ...SHAPE_STYLES.NONE,
    fillColor: COLORS.TRANSPARENT,
    strokeWeight: 2,
    strokeColor: COLORS.WHITE_100,
  },
  [ShapePresetStyle.CUSTOM]: {
    ...SHAPE_STYLES.NONE,
  },
};

export function controller(): GameObjectController {
  return {
    getNew: getZeroShapeObject,
    sync,
    update,
    createPackage,
  };
}

export function getZeroShapeObject(ent: Entity): ShapeObject {
  return {
    ...ent,
    objectType: ObjectType.SHAPE,
    shapeType: ShapeType.RECTANGLE,
    style: ShapePresetStyle.CUSTOM,
    cornerRadius: 0,
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
    styleFillColor: COLORS.TRANSPARENT,
    styleLineWeight: 0,
    styleLineColor: COLORS.TRANSPARENT,
    lineStartType: LineEndpointType.NONE,
    lineEndType: LineEndpointType.NONE,
  };
}

export function sync(manager: GameManager, obj: ShapeObject, props: ServerEvent_Shape) {
  if (!obj.id) {
    addGameObject(manager, obj);
    setParentByEntityProps(manager, obj, props.sId, props.roomId, props.entityProps || {});
    setVisibilityState(obj, VisibilityState.DEFAULT);
  }

  if ('shapeType' in props && props.shapeType !== undefined) {
    obj.shapeType = props.shapeType;
  }
  if ('style' in props && props.style !== undefined) {
    obj.style = props.style;
  }
  if ('width' in props && props.width !== undefined) {
    obj.width = props.width;
  }
  if ('height' in props && props.height !== undefined) {
    obj.height = props.height;
  }
  if ('cornerRadius' in props && props.cornerRadius !== undefined) {
    obj.cornerRadius = props.cornerRadius;
  }
  if ('radius' in props && props.radius !== undefined) {
    obj.radius = props.radius;
  }
  if ('startX' in props && props.startX !== undefined) {
    obj.startX = props.startX;
  }
  if ('startY' in props && props.startY !== undefined) {
    obj.startY = props.startY;
  }
  if ('endX' in props && props.endX !== undefined) {
    obj.endX = props.endX;
  }
  if ('endY' in props && props.endY !== undefined) {
    obj.endY = props.endY;
  }
  if ('styleFillColor' in props && props.styleFillColor !== undefined) {
    obj.styleFillColor = parseColorProp(props.styleFillColor);
  }
  if ('styleLineWeight' in props && props.styleLineWeight !== undefined) {
    obj.styleLineWeight = props.styleLineWeight;
  }
  if ('styleLineColor' in props && props.styleLineColor !== undefined) {
    obj.styleLineColor = parseColorProp(props.styleLineColor);
  }
  if ('lineStartType' in props && props.lineStartType !== undefined) {
    obj.lineStartType = props.lineStartType;
  }
  if ('lineEndType' in props && props.lineEndType !== undefined) {
    obj.lineEndType = props.lineEndType;
  }
}

function parseColorProp(value: string): Color {
  if (!value) {
    return COLORS.TRANSPARENT;
  }
  const result = hexToColor(value);

  return result.valid ? result.color : COLORS.TRANSPARENT;
}

export function update(_obj: ShapeObject): boolean {
  return false;
}

export function createPackage(obj: ShapeObject, position: V2, scale: number): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];

  const shapeStyle =
    obj.style === ShapePresetStyle.CUSTOM
      ? {
          ...SHAPE_STYLES.NONE,
          fillColor: obj.styleFillColor,
          strokeWeight: obj.styleLineWeight,
          strokeColor: obj.styleLineColor,
        }
      : PRESET_STYLES[obj.style];

  if (obj.shapeType === ShapeType.RECTANGLE) {
    const rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      positionX: position.x,
      positionY: position.y,
      scale,
      width: obj.width,
      height: obj.height,
      shapeStyle,
      cornerRadius1: obj.cornerRadius,
      cornerRadius2: obj.cornerRadius,
      cornerRadius3: obj.cornerRadius,
      cornerRadius4: obj.cornerRadius,
      shadow: null,
    };
    rPackage.push(rect);
  } else if (obj.shapeType === ShapeType.CIRCLE) {
    const circle: RenderPackageCircle = {
      type: RenderItemType.circle,
      ...EMPTY_RENDER_TRANSFORM,
      positionX: position.x,
      positionY: position.y,
      scale,
      radius: obj.radius,
      shapeStyle,
      shadow: null,
    };
    rPackage.push(circle);
  } else if (obj.shapeType === ShapeType.LINE) {
    const lineColor =
      obj.style === ShapePresetStyle.CUSTOM ? obj.styleLineColor : shapeStyle.strokeColor;
    const lineWeight =
      obj.style === ShapePresetStyle.CUSTOM
        ? obj.styleLineWeight > 0
          ? obj.styleLineWeight
          : LINES.DEFAULT.width
        : shapeStyle.strokeWeight;

    const dx = obj.endX - obj.startX;
    const dy = obj.endY - obj.startY;
    const len = Math.sqrt(dx * dx + dy * dy);

    let lineStartX = obj.startX;
    let lineStartY = obj.startY;
    let lineEndX = obj.endX;
    let lineEndY = obj.endY;

    if (len > 0) {
      const nx = dx / len;
      const ny = dy / len;
      const arrowLength = lineWeight * 5;

      if (obj.lineStartType === LineEndpointType.CLOSED_ARROW) {
        lineStartX = obj.startX + nx * arrowLength;
        lineStartY = obj.startY + ny * arrowLength;
      }
      if (obj.lineEndType === LineEndpointType.CLOSED_ARROW) {
        lineEndX = obj.endX - nx * arrowLength;
        lineEndY = obj.endY - ny * arrowLength;
      }
    }

    const line: RenderPackageLine = {
      type: RenderItemType.line,
      ...EMPTY_RENDER_TRANSFORM,
      positionX: position.x,
      positionY: position.y,
      scale,
      posA: v2(lineStartX, lineStartY),
      posB: v2(lineEndX, lineEndY),
      style: { ...LINES.DEFAULT, color: lineColor, width: lineWeight },
    };

    rPackage.push(line);

    if (len > 0) {
      const nx = dx / len;
      const ny = dy / len;

      if (obj.lineStartType === LineEndpointType.CLOSED_ARROW) {
        rPackage.push(
          buildClosedArrow(
            position,
            scale,
            obj.startX,
            obj.startY,
            -nx,
            -ny,
            lineColor,
            lineWeight,
          ),
        );
      } else if (obj.lineStartType === LineEndpointType.OPEN_ARROW) {
        rPackage.push(
          buildOpenArrow(position, scale, obj.startX, obj.startY, -nx, -ny, lineColor, lineWeight),
        );
      } else if (obj.lineStartType === LineEndpointType.DOT) {
        rPackage.push(buildLineDot(position, scale, obj.startX, obj.startY, lineColor, lineWeight));
      }

      if (obj.lineEndType === LineEndpointType.CLOSED_ARROW) {
        rPackage.push(
          buildClosedArrow(position, scale, obj.endX, obj.endY, nx, ny, lineColor, lineWeight),
        );
      } else if (obj.lineEndType === LineEndpointType.OPEN_ARROW) {
        rPackage.push(
          buildOpenArrow(position, scale, obj.endX, obj.endY, nx, ny, lineColor, lineWeight),
        );
      } else if (obj.lineEndType === LineEndpointType.DOT) {
        rPackage.push(buildLineDot(position, scale, obj.endX, obj.endY, lineColor, lineWeight));
      }
    }
  }

  return rPackage;
}

